/**
 * Filtrarea, sortarea si gruparea ecranului zilei.
 *
 * Stau aici, ca functii PURE peste `DayEntryDto[]`, din trei motive:
 *
 *   1. Se pot testa fara sa randam nimic — iar „de ce nu-mi apare furnizorul X in listă" e
 *      intrebarea care se va pune cel mai des, si trebuie sa aiba un raspuns verificabil.
 *   2. Filtrarea se face pe CLIENT deliberat: ruta `/api/zi/:data` intoarce toata ziua (12-33 de
 *      randuri), deci un filtru nou nu costa un round-trip. Un `q` trimis la server ar face grila sa
 *      clipeasca la fiecare litera tastata.
 *   3. Aceleasi functii dau si contorul „N filtre active", pe care starea goala il foloseste ca sa
 *      ofere `Sterge filtrele (N)` — o grila filtrata care arata zero e cel mai frecvent „bug"
 *      raportat intr-un instrument de trezorerie.
 *
 * Cautarea trece prin `foldRo`, deci `sameday` gaseste `SAMEDAY`, `Ромania` nu, dar `Romania`
 * gaseste `ROMÂNIA` — si tot ea gaseste mojibake-ul din sursa (`RomÃ¢nia`), pentru ca se cauta si
 * in forma needitata a numelui.
 */

import { compareRo, foldRo } from '@evomag/core/format';
import type { DayEntryDto } from '../../hooks/useZi.ts';
import type { CodStare } from '../../lib/stari.ts';

export type CheieSortare = 'stare' | 'furnizor' | 'suma' | 'facturi' | 'banca' | 'sold' | 'stoc';
export type CheieGrupare = 'niciuna' | 'banca' | 'categorie' | 'stare';

export interface FiltreZi {
  stare?: readonly CodStare[] | undefined;
  banca?: readonly string[] | undefined;
  valuta?: readonly string[] | undefined;
  categ?: readonly string[] | undefined;
  q?: string | undefined;
  /** Prag minim, in unitati MAJORE (asa se scrie in URL); conversia la minor se face aici. */
  min?: number | undefined;
}

/** Eticheta de banca a unui rand: codul derivat din IBAN, cu caderea pe coloana bruta. */
export function etichetaBanca(intrare: DayEntryDto): string {
  return intrare.bankCode ?? intrare.bancaRaw ?? '—';
}

export function etichetaCategorie(intrare: DayEntryDto): string {
  return intrare.categoryName ?? 'Fără categorie';
}

/** Textul in care cauta `q`. Include CUI-ul si facturile: „factura 260600841" e o cautare reala. */
function textCautabil(intrare: DayEntryDto): string {
  return foldRo(
    [
      intrare.supplierName,
      intrare.cuiNorm ?? '',
      intrare.bancaRaw ?? '',
      intrare.categoryName ?? '',
      intrare.invoiceRefs.map((r) => `${r.raw} ${r.normalized}`).join(' '),
    ].join(' '),
  );
}

/** Cate filtre sunt efectiv active. Alimenteaza `Sterge filtrele (N)` din starea goala. */
export function numaraFiltre(filtre: FiltreZi): number {
  let n = 0;
  if (filtre.stare && filtre.stare.length > 0) n += 1;
  if (filtre.banca && filtre.banca.length > 0) n += 1;
  if (filtre.valuta && filtre.valuta.length > 0) n += 1;
  if (filtre.categ && filtre.categ.length > 0) n += 1;
  if (filtre.q && filtre.q.trim() !== '') n += 1;
  if (typeof filtre.min === 'number') n += 1;
  return n;
}

export function filtreaza(
  intrari: readonly DayEntryDto[],
  filtre: FiltreZi,
): readonly DayEntryDto[] {
  const stari = filtre.stare && filtre.stare.length > 0 ? new Set<string>(filtre.stare) : null;
  const banci = filtre.banca && filtre.banca.length > 0 ? new Set(filtre.banca) : null;
  const valute = filtre.valuta && filtre.valuta.length > 0 ? new Set(filtre.valuta) : null;
  const categorii = filtre.categ && filtre.categ.length > 0 ? new Set(filtre.categ) : null;
  const cautare = filtre.q?.trim() ? foldRo(filtre.q.trim()) : null;
  // Pragul se compara in unitati minore: `min=5000` inseamna 5.000 lei, nu 50 lei.
  const pragMinor = typeof filtre.min === 'number' ? Math.round(filtre.min * 100) : null;

  return intrari.filter((e) => {
    if (stari && !stari.has(e.status)) return false;
    if (banci && !banci.has(etichetaBanca(e))) return false;
    if (valute && !valute.has(e.currency)) return false;
    if (categorii && !categorii.has(etichetaCategorie(e))) return false;
    // Pragul se aplica pe suma in LEI: altfel 2.000 EUR ar cadea sub un prag de 5.000.
    if (pragMinor !== null && e.amountRonMinor < pragMinor) return false;
    if (cautare && !textCautabil(e).includes(cautare)) return false;
    return true;
  });
}

/**
 * Ordinea starilor la sortare: aceeasi cu cea din legenda, ca `sort=stare` sa produca gruparea pe
 * care ochiul o asteapta din culori. Starile din afara legendei cad la coada.
 */
const ORDINE_STARE: Readonly<Record<string, number>> = {
  approved: 1,
  urgent: 2,
  blocked: 3,
  staged: 4,
  waiting: 5,
  verify: 6,
};

export function sorteaza(
  intrari: readonly DayEntryDto[],
  cheie: CheieSortare | undefined,
): readonly DayEntryDto[] {
  if (!cheie || cheie === 'furnizor') {
    return [...intrari].sort((a, b) => compareRo(a.supplierName, b.supplierName));
  }
  const copie = [...intrari];
  switch (cheie) {
    case 'suma':
      // Descrescator: la sortarea dupa suma intrebarea e mereu „ce e mare", nu „ce e mic".
      return copie.sort((a, b) => b.amountRonMinor - a.amountRonMinor);
    case 'stare':
      return copie.sort(
        (a, b) =>
          (ORDINE_STARE[a.status] ?? 99) - (ORDINE_STARE[b.status] ?? 99) ||
          compareRo(a.supplierName, b.supplierName),
      );
    case 'banca':
      return copie.sort(
        (a, b) =>
          compareRo(etichetaBanca(a), etichetaBanca(b)) ||
          compareRo(a.supplierName, b.supplierName),
      );
    case 'sold':
      // Descrescator, ca la suma: intrebarea e „cine are sold mare", nu invers. Randurile fara
      // snapshot merg la coada — lipsa unei date nu e o valoare mica.
      return copie.sort(
        (a, b) => (b.soldMinor ?? -1) - (a.soldMinor ?? -1) || compareRo(a.supplierName, b.supplierName),
      );
    case 'stoc':
      return copie.sort(
        (a, b) => (b.stocMinor ?? -1) - (a.stocMinor ?? -1) || compareRo(a.supplierName, b.supplierName),
      );
    case 'facturi':
      // Randurile FARA factura primele: ele sunt cele care au nevoie de interventie.
      return copie.sort(
        (a, b) =>
          a.invoiceRefCount - b.invoiceRefCount || compareRo(a.supplierName, b.supplierName),
      );
  }
}

export interface GrupZi {
  cheie: string;
  eticheta: string;
  intrari: readonly DayEntryDto[];
  totalRonMinor: number;
}

/**
 * Gruparea pastreaza ORDINEA deja sortata in interiorul fiecarui grup, iar grupurile se ordoneaza
 * dupa eticheta. `niciuna` intoarce un singur grup fara antet — asa componenta nu are doua cai de
 * randare, deci nu poate avea doua comportamente.
 */
export function grupeaza(
  intrari: readonly DayEntryDto[],
  cheie: CheieGrupare | undefined,
  etichetaStare: (cod: string) => string,
): readonly GrupZi[] {
  const total = (lista: readonly DayEntryDto[]): number =>
    lista.reduce((s, e) => s + e.amountRonMinor, 0);

  if (!cheie || cheie === 'niciuna') {
    return [{ cheie: 'toate', eticheta: '', intrari, totalRonMinor: total(intrari) }];
  }

  const galeti = new Map<string, DayEntryDto[]>();
  for (const e of intrari) {
    const k =
      cheie === 'banca'
        ? etichetaBanca(e)
        : cheie === 'categorie'
          ? etichetaCategorie(e)
          : e.status;
    const lista = galeti.get(k);
    if (lista) lista.push(e);
    else galeti.set(k, [e]);
  }

  return [...galeti.entries()]
    .map(([k, lista]) => ({
      cheie: k,
      eticheta: cheie === 'stare' ? etichetaStare(k) : k,
      intrari: lista as readonly DayEntryDto[],
      totalRonMinor: total(lista),
    }))
    .sort((a, b) =>
      cheie === 'stare'
        ? (ORDINE_STARE[a.cheie] ?? 99) - (ORDINE_STARE[b.cheie] ?? 99)
        : compareRo(a.eticheta, b.eticheta),
    );
}

/** Valorile distincte pentru chip-urile de filtrare, calculate din ziua INTREAGA, nu din cea filtrata. */
export function optiuniFiltre(intrari: readonly DayEntryDto[]): {
  banci: readonly string[];
  valute: readonly string[];
  categorii: readonly string[];
} {
  const banci = new Set<string>();
  const valute = new Set<string>();
  const categorii = new Set<string>();
  for (const e of intrari) {
    banci.add(etichetaBanca(e));
    valute.add(e.currency);
    categorii.add(etichetaCategorie(e));
  }
  return {
    banci: [...banci].sort(compareRo),
    valute: [...valute].sort(compareRo),
    categorii: [...categorii].sort(compareRo),
  };
}

/**
 * Platile fara curs.
 *
 * O suma in valuta fara curs NU intra in `De plata` — dar nici nu se pierde: se numara pe o linie
 * proprie. Un `SUM()` care sare peste celule goale in tacere e exact felul in care totalul zilei
 * devine discret gresit.
 */
export function faraCurs(intrari: readonly DayEntryDto[]): readonly DayEntryDto[] {
  return intrari.filter(
    (e) =>
      e.currency !== 'RON' &&
      e.manualRateE6 === null &&
      e.planningRateE6 === null &&
      e.amountRonMinor === 0,
  );
}
